import React from 'react';
import { Modal } from 'react-bootstrap';
import type { GlowpickProduct, Ingredient } from '../../../../models/Glowpick';

interface IngredientsModalProps {
  show: boolean;
  onHide: () => void;
  product: GlowpickProduct | null;
}

export const IngredientsModal: React.FC<IngredientsModalProps> = ({ show, onHide, product }) => {
  if (!product) {
    return null;
  }

  // Process ingredients from the new data structure
  const processIngredients = (ingredients: Ingredient[]) => {
    if (!ingredients || !Array.isArray(ingredients)) {
      return [];
    }

    return ingredients.map((ingredient) => {
      // Handle both string and numeric risk values
      const riskValue = typeof ingredient.risk === 'string' ? parseInt(ingredient.risk, 10) : ingredient.risk || 1;
      let riskLevel = 'low';

      // Map numeric risk to risk level
      if (riskValue >= 7) {
        riskLevel = 'high';
      } else if (riskValue >= 3) {
        riskLevel = 'mid';
      } else {
        riskLevel = 'low';
      }

      return {
        korean: ingredient.kor || '',
        english: ingredient.eng || '',
        risk: riskLevel,
        score: riskValue,
        desc: ingredient.desc || '성분 설명 없음',
      };
    });
  };

  const ingredients = processIngredients(product.ingredients || []);

  // Calculate risk counts
  const riskCounts = {
    unknown: ingredients.filter((i) => i.risk === 'unknown').length,
    low: ingredients.filter((i) => i.risk === 'low').length,
    mid: ingredients.filter((i) => i.risk === 'mid').length,
    high: ingredients.filter((i) => i.risk === 'high').length,
  };

  const mediumHighCount = riskCounts.mid + riskCounts.high;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>성분 구성 - {product.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {/* Risk Summary */}
          <article className="mb-4">
            <p className="mb-3">
              중간위험도 이상의 성분
              <span className="text-danger fw-bold ms-1">{mediumHighCount}</span>개
            </p>

            {/* Risk Legends */}
            <div className="d-flex flex-wrap gap-3 mb-3">
              <div className="d-flex align-items-center">
                <div
                  className="me-2 px-2 py-1 rounded text-white text-center"
                  style={{ backgroundColor: '#6c757d', minWidth: '40px', fontSize: '0.75rem' }}
                >
                  {riskCounts.unknown}개
                </div>
                <small className="text-muted">위험도 미정</small>
              </div>
              <div className="d-flex align-items-center">
                <div
                  className="me-2 px-2 py-1 rounded text-white text-center"
                  style={{ backgroundColor: '#28a745', minWidth: '40px', fontSize: '0.75rem' }}
                >
                  {riskCounts.low}개
                </div>
                <small className="text-muted">낮은 위험도</small>
              </div>
              <div className="d-flex align-items-center">
                <div
                  className="me-2 px-2 py-1 rounded text-white text-center"
                  style={{ backgroundColor: '#ffc107', minWidth: '40px', fontSize: '0.75rem' }}
                >
                  {riskCounts.mid}개
                </div>
                <small className="text-muted">중간 위험도</small>
              </div>
              <div className="d-flex align-items-center">
                <div
                  className="me-2 px-2 py-1 rounded text-white text-center"
                  style={{ backgroundColor: '#dc3545', minWidth: '40px', fontSize: '0.75rem' }}
                >
                  {riskCounts.high}개
                </div>
                <small className="text-muted">높은 위험도</small>
              </div>
            </div>
          </article>

          {/* Guidelines */}
          <article className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p className="small text-success mb-1">EWG 기준에 따른 제품 성분에 대한 위험 점수입니다.</p>
            <p className="small text-muted mb-0">
              위험 점수표 : 점수 미정(위험도 미정), 1~2점(낮은 위험도), 3~6점(중간 위험도), 7~10점(높은 위험도)
            </p>
          </article>

          {/* Ingredient List */}
          <div className="ingredient-list">
            {ingredients.length > 0 ? (
              ingredients.map((ingredient, index) => (
                <div key={index} className="d-flex align-items-start mb-3 p-3 border rounded">
                  <div className="me-3 flex-shrink-0">
                    <div
                      className={`px-2 py-1 rounded text-white text-center fw-bold`}
                      style={{
                        backgroundColor:
                          ingredient.risk === 'low'
                            ? '#28a745'
                            : ingredient.risk === 'mid'
                              ? '#ffc107'
                              : ingredient.risk === 'high'
                                ? '#dc3545'
                                : '#6c757d',
                        minWidth: '40px',
                        fontSize: '0.75rem',
                      }}
                    >
                      {ingredient.score}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <p className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                      {ingredient.korean}
                    </p>
                    <p className="text-muted small mb-1">{ingredient.english}</p>
                    <p className="small text-muted mb-0">{ingredient.desc}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-4">
                <p>성분 정보가 없습니다.</p>
              </div>
            )}
          </div>

          {/* Total Count */}
          <div className="mt-4 pt-3 border-top">
            <h6 className="small text-muted mb-2">성분 요약</h6>
            <p className="small">
              총 <strong>{ingredients.length}</strong>개의 성분이 포함되어 있습니다.
            </p>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
